import { Button, Collapse, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';

type PromoActionBarProps = {
    name: string;
    menuItems: MenuProps['items'];
};

const PromoActionBar = ({ name, menuItems }: PromoActionBarProps) => {
    return (
        <div className="flex flex-row py-2 px-3 items-center justify-between border border-[#E4E4E7] rounded-lg bg-[#FAFAFA]">
            <p className="text-sm font-semibold">{name}</p>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                overlayStyle={{
                    width: '10.9375rem',
                    borderRadius: '0.5rem',
                }}
            >
                <span className="cursor-pointer">
                    <EllipsisOutlined />
                </span>
            </Dropdown>
        </div>
    );
};

type PromoItem = {
    promo_id: string;
    promo_name: string;
};

type EventPromoSectionProps = {
    isEventSaved?: boolean;
    promos?: PromoItem[];
    onAddPromo?: () => void;
    onEditPromo?: () => void;
    onDeletePromo?: (promoId: string) => void;
};

const PromoDetailsEmptyState = ({ isEventSaved, promos = [], onAddPromo, onDeletePromo }: EventPromoSectionProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { setEditPromoId } = usePlanDetailsContext();

    const getMenuItems = (promoId: string): MenuProps['items'] => [
        {
            label: tpoGenConfig.labels.common.edit,
            key: 'edit',
            onClick: () => setEditPromoId(promoId),
        },
        {
            label: <span className="text-[#FF4D4F]">{tpoGenConfig.labels.common.delete}</span>,
            key: 'delete',
            onClick: () => onDeletePromo?.(promoId),
        },
    ];

    return (
        <div>
            <Button
                type="primary"
                disabled={!isEventSaved}
                onClick={() => onAddPromo?.()}
                icon={<PlusCircleOutlined />}
                style={{ display: 'flex', alignItems: 'center' }}
            >
                {tpoGenConfig.labels.promos.addPromo}
            </Button>

            {promos.length === 0 ? (
                <div className="my-8 flex flex-row justify-center items-center">
                    <p className="text-sm text-[#9F9FA9]">{tpoGenConfig.labels.promos.noPromotionsAdded}</p>
                </div>
            ) : (
                <div className="flex flex-col gap-y-2 my-4">
                    {promos.map((promo) => (
                        <PromoActionBar
                            key={promo.promo_id}
                            name={promo.promo_name || promo.promo_id}
                            menuItems={getMenuItems(promo.promo_id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const EventPromoSection = ({
    isEventSaved,
    promos,
    onAddPromo,
    onEditPromo,
    onDeletePromo,
}: EventPromoSectionProps) => {
    const tpoGenConfig = useTpoGenConfig();
    return (
        <div>
            <Collapse expandIconPosition="end" defaultActiveKey={['1']} style={{ backgroundColor: '#FFF' }}>
                <Collapse.Panel header={<p className="text-base font-semibold">{tpoGenConfig.labels.promos.sectionTitle}</p>} key="1">
                    <PromoDetailsEmptyState
                        isEventSaved={isEventSaved}
                        promos={promos}
                        onAddPromo={onAddPromo}
                        onEditPromo={onEditPromo}
                        onDeletePromo={onDeletePromo}
                    />
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default EventPromoSection;
